import { hashQuery } from '../../App';
import { useAllPages, useLoad } from '../../data';
import { InkHero, Loading, MoneyText, Panel, PageHead, Tabs, fmtMoney, formatWhen, downloadCsv } from '../../ui';
import { Booking, Money } from '../../types';

// Reports (canon 09 hotel Overview, flow H money). The hotel's margin on
// completed bookings, by supplier and period, from the hotel's own partition
// only — the hotel sees guest revenue, supplier cost, margin AND the ISO
// commission accrued as "fees" (canon 10 Money rule, DECISION-016 /
// DECISION-052). A supplier never sees any of it, and the fee lives on the
// hotel row only, so gross is never disturbed.
interface SupplierMargin {
  supplierAccountId: string;
  supplierName?: string;
  jobs: number;
  guestRevenue: Money;
  margin: Money;
  fees: Money;
}
interface Reports {
  from: string;
  to: string;
  completed: {
    jobs: number;
    guestRevenue: Money;
    supplierCost: Money;
    margin: Money;
    fees: Money;
    bySupplier: SupplierMargin[];
  };
  upcoming: { jobs: number; guestRevenue: Money; margin: Money };
  // Fees the hotel paid for its own late cancellations (P10 / DECISION-058) — its
  // own cost line, never netted into margin.
  cancellationFees: { jobs: number; total: Money };
}

type Period = 'month' | 'year';

function windowFor(period: Period): { from: string; to: string } {
  const d = new Date();
  const y = d.getFullYear();
  if (period === 'month') {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

export function Reports() {
  const period: Period = hashQuery().get('p') === 'month' ? 'month' : 'year';
  const { from, to } = windowFor(period);
  const summary = useLoad<Reports>(`/reports?from=${from}&to=${to}`);
  const completed = useAllPages<Booking>(`/bookings?status=completed&from=${from}&to=${to}`);
  // Cancelled jobs that carried a cancellation fee the hotel paid (P10) — a cost,
  // so the statement reconciles EVERY euro (canon S6), on its own labelled line,
  // never netted into margin.
  const cancelledWithFee = useAllPages<Booking>(`/bookings?status=cancelled&from=${from}&to=${to}`);
  const r = summary.data;
  const jobs = completed.items.slice().sort((a, b) => Date.parse(b.start) - Date.parse(a.start));
  const feeRows = cancelledWithFee.items.filter((b) => b.cancelFee);

  const exportCsv = () =>
    downloadCsv(
      `reports-${from}-to-${to}.csv`,
      ['Date', 'Product', 'Supplier', 'Guest revenue', 'Supplier cost', 'Margin', 'ISO fees', 'Currency'],
      [
        ...jobs.map((b) => {
          const guest = b.guestAmount?.minor ?? 0;
          const margin = b.margin?.minor ?? 0;
          return [
            b.start.slice(0, 10),
            b.productName ?? '',
            b.supplierName ?? '',
            (guest / 100).toFixed(2),
            ((guest - margin) / 100).toFixed(2),
            (margin / 100).toFixed(2),
            ((b.commission?.minor ?? 0) / 100).toFixed(2),
            b.guestAmount?.currency ?? b.supplierAmount.currency,
          ];
        }),
        ...feeRows.map((b) => [
          b.start.slice(0, 10),
          `${b.productName ?? 'Booking'} — cancellation fee`,
          b.supplierName ?? '',
          '0.00',
          (b.cancelFee!.minor / 100).toFixed(2),
          '0.00',
          '0.00',
          b.cancelFee!.currency,
        ]),
      ],
    );

  return (
    <div>
      <PageHead title="Reports" sub="Margin earned on completed jobs — guest revenue, supplier cost and your margin.">
        <button className="btn ghost" onClick={exportCsv} disabled={!jobs.length}>
          Download CSV
        </button>
      </PageHead>

      <Tabs
        items={[
          { label: 'This month', href: '#/reports?p=month', active: period === 'month' },
          { label: 'This year', href: '#/reports?p=year', active: period === 'year' },
        ]}
      />

      <InkHero
        eyebrow={`Margin · ${period === 'year' ? 'this year' : 'this month'}`}
        figure={r ? fmtMoney(r.completed.margin) : '…'}
        explain={
          r
            ? `${r.completed.jobs} job${r.completed.jobs === 1 ? '' : 's'} completed · guest revenue ${fmtMoney(r.completed.guestRevenue)} · supplier cost ${fmtMoney(r.completed.supplierCost)} · ISO fees ${fmtMoney(r.completed.fees)}`
            : ' '
        }
      />

      {r && r.cancellationFees.jobs > 0 && (
        <Panel title="Cancellation fees paid" meta="a cost · its own line">
          <p style={{ marginBottom: 6 }}>
            <MoneyText money={r.cancellationFees.total} /> across {r.cancellationFees.jobs} late
            cancellation{r.cancellationFees.jobs === 1 ? '' : 's'}.
          </p>
          <p className="hint">
            Confirmed jobs you cancelled close to the start — this compensates the supplier for the held
            slot. It is a cost, shown on its own and never netted into your margin.
          </p>
        </Panel>
      )}

      <Panel
        title="By supplier"
        meta={r ? `${r.completed.bySupplier.length} supplier${r.completed.bySupplier.length === 1 ? '' : 's'}` : ''}
        flush
      >
        {!r ? (
          <div className="card-body">
            {summary.error ? <p className="error-line">{summary.error}</p> : <Loading />}
          </div>
        ) : r.completed.bySupplier.length === 0 ? (
          <div className="card-body">
            <div className="empty-neutral">No completed jobs in this period yet.</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Supplier</th>
                <th className="num">Guest revenue</th>
                <th className="num">Margin</th>
                <th className="num">ISO fees</th>
              </tr>
            </thead>
            <tbody>
              {r.completed.bySupplier.map((s) => (
                <tr key={s.supplierAccountId}>
                  <td>
                    {s.supplierName ?? s.supplierAccountId}
                    <br />
                    <span className="hint">
                      {s.jobs} job{s.jobs === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td className="num">
                    <MoneyText money={s.guestRevenue} />
                  </td>
                  <td className="num">
                    <MoneyText money={s.margin} />
                  </td>
                  <td className="num">
                    <MoneyText money={s.fees} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Completed jobs" meta="every booking, its margin" flush>
        {jobs.length === 0 ? (
          <div className="card-body">
            <div className="empty-neutral">Nothing completed in this period.</div>
          </div>
        ) : (
          <table>
            <tbody>
              {jobs.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">{b.supplierName}</span>
                  </td>
                  <td className="num">
                    <MoneyText money={b.margin} />
                  </td>
                  <td className="num">
                    {b.commission ? <MoneyText money={b.commission} /> : <span className="hint">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
